/**
 * Graph-based builder for the action registry.
 *
 * Resources are independent nodes created via `resource()`. Parent
 * relationships are explicit graph edges via `.childOf()`. Actions are
 * attached via the `.addAction()` method, which infers the entire action
 * map in one shot. `buildRegistry()` converts an array of
 * ActionAttachments into the processed output.
 */

import type { ProcessedRegistryOutput } from './action-registry-types.js';

// ===== ActionDef =====

export interface ActionDef {
  readonly description: string;
  readonly permission?: boolean;
  /** Parent actions whose permission implicitly grants this action's permission.
   *  Use a plain action name for same-resource refs (e.g. 'Update'),
   *  or [resource, 'Action'] tuple for cross-resource refs (e.g. [matter, 'Create']). */
  readonly delegatedBy?: readonly (string | readonly [{ _name: string }, string])[];
  readonly audit?: boolean;
  readonly tracking?: boolean;
  readonly legacyTrackingName?: string;
  /** Whether this permission can be overridden at the matter level.
   *  Defaults to true for resources that are descendants of (or equal to) 'matter'.
   *  Set explicitly to override the default. Only meaningful when permission is true. */
  readonly matterScoped?: boolean;
}

// ===== Shared types =====

export interface ActionEntry {
  resource: string;
  action: string;
  description: string;
  permission: boolean;
  auditable: boolean;
  auditString?: string;
}

export interface RegistryOutput {
  entries: Record<string, ActionEntry>;
  permissions: Record<string, string>;
  trackingEvents: Record<string, string>;
  auditEvents: Record<string, string>;
  displayNames: Record<string, string>;
  descriptions: Record<string, string>;
  hierarchy: Record<string, string | null>;
  delegatedBy: Record<string, string[]>;
  /** Set of permission values (e.g. 'document:upload') that can be overridden at the matter level. */
  matterScopedPermissions: Set<string>;
}

// ===== ResourceNode =====

export class ResourceNode<
  Name extends string = string,
  DisplayName extends string = string,
  Parent extends string | null = string | null,
> {
  readonly _name: Name;
  readonly _displayName: DisplayName;
  readonly _parent: Parent;

  constructor(name: Name, displayName: DisplayName, parent: Parent) {
    this._name = name;
    this._displayName = displayName;
    this._parent = parent;
  }

  childOf<P extends string>(parent: { _name: P }): ResourceNode<Name, DisplayName, P> {
    return new ResourceNode(this._name, this._displayName, parent._name);
  }

  /** Attach actions to this resource node, inferring the full action map in one shot */
  addAction<const A extends Record<string, ActionDef>>(
    actionDefs: A,
  ): ActionAttachment<Name, DisplayName, Parent, A> {
    return {
      _name: this._name,
      _displayName: this._displayName,
      _parent: this._parent,
      _actions: actionDefs,
    };
  }
}

// ===== ActionAttachment =====

export interface ActionAttachment<
  Name extends string = string,
  DisplayName extends string = string,
  Parent extends string | null = string | null,
  Actions extends Record<string, ActionDef> = Record<string, ActionDef>,
> {
  readonly _name: Name;
  readonly _displayName: DisplayName;
  readonly _parent: Parent;
  readonly _actions: Actions;
}

/** Create a new resource node */
export function resource<N extends string, D extends string>(
  name: N,
  displayName: D,
): ResourceNode<N, D, null> {
  return new ResourceNode(name, displayName, null);
}

// ===== Runtime processing =====

function toPascalCase(s: string): string {
  return s
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join('');
}

function toKebabCase(s: string): string {
  return s.replace(/[A-Z]/g, (letter, index) => (index > 0 ? '-' : '') + letter.toLowerCase());
}

function deriveKey(resourceName: string, action: string): string {
  return toPascalCase(resourceName) + toPascalCase(action);
}

function computeAuditString(resourceName: string, action: string): string {
  return `${resourceName}:${toKebabCase(action)}`;
}

function resolveDelegatedBy(
  nodes: readonly ActionAttachment[],
  permissions: Record<string, string>,
): Record<string, string[]> {
  const delegatedBy: Record<string, string[]> = {};
  for (const node of nodes) {
    for (const [actionName, config] of Object.entries(node._actions)) {
      if (config.delegatedBy && config.delegatedBy.length > 0) {
        const childKey = deriveKey(node._name, actionName);
        const childPerm = permissions[childKey] as string | undefined;
        if (childPerm === undefined) {
          throw new Error(
            `Action '${actionName}' on '${node._name}' has delegatedBy but no permission`,
          );
        }
        const parentPerms: string[] = [];
        for (const ref of config.delegatedBy) {
          let parentKey: string;
          if (typeof ref === 'string') {
            parentKey = deriveKey(node._name, ref);
          } else {
            const [parentResource, parentAction] = ref;
            parentKey = deriveKey(parentResource._name, parentAction);
          }
          const parentPerm = permissions[parentKey] as string | undefined;
          if (parentPerm === undefined) {
            throw new Error(
              `Invalid delegatedBy ref on '${node._name}.${actionName}': parent permission '${typeof ref === 'string' ? ref : `${ref[0]._name}.${ref[1]}`}' not found`,
            );
          }
          parentPerms.push(parentPerm);
        }
        delegatedBy[childPerm] = parentPerms;
      }
    }
  }
  return delegatedBy;
}

/** Check whether a resource is 'matter' or a descendant of 'matter' in the hierarchy. */
function isDescendantOfMatter(
  resourceName: string,
  hierarchy: Record<string, string | null>,
): boolean {
  let current: string | null = resourceName;
  while (current !== null) {
    if (current === 'matter') return true;
    current = hierarchy[current] ?? null;
  }
  return false;
}

function processRegistry(nodes: readonly ActionAttachment[]): RegistryOutput {
  const entries: Record<string, ActionEntry> = {};
  const permissions: Record<string, string> = {};
  const auditEvents: Record<string, string> = {};
  const trackingEvents: Record<string, string> = {};
  const displayNames: Record<string, string> = {};
  const descriptions: Record<string, string> = {};
  const hierarchy: Record<string, string | null> = {};
  const matterScopedPermissions = new Set<string>();

  // First pass: build hierarchy
  for (const node of nodes) {
    hierarchy[node._name] = node._parent;
  }

  for (const node of nodes) {
    displayNames[node._name] = node._displayName;

    const resourceIsMatterDescendant = isDescendantOfMatter(node._name, hierarchy);

    for (const [actionName, config] of Object.entries(node._actions)) {
      const key = deriveKey(node._name, actionName);
      const isPermission = config.permission === true;
      const isAuditable = config.audit === true;

      const entry: ActionEntry = {
        resource: node._name,
        action: toKebabCase(actionName),
        description: config.description,
        permission: isPermission,
        auditable: isAuditable,
      };

      if (isAuditable) {
        const auditString = computeAuditString(node._name, actionName);
        entry.auditString = auditString;
        auditEvents[key] = auditString;
      }

      entries[key] = entry;

      if (isPermission) {
        const permissionValue = `${node._name}:${toKebabCase(actionName)}`;
        permissions[key] = permissionValue;
        descriptions[permissionValue] = config.description;

        // Determine matter-scoped: explicit override takes precedence, else derive from hierarchy
        const isMatterScoped = config.matterScoped ?? resourceIsMatterDescendant;
        if (isMatterScoped) {
          matterScopedPermissions.add(permissionValue);
        }
      }

      if (config.tracking === true) {
        trackingEvents[key] = config.legacyTrackingName ?? actionName;
      }
    }
  }

  return {
    entries,
    permissions,
    auditEvents,
    trackingEvents,
    displayNames,
    descriptions,
    hierarchy,
    delegatedBy: resolveDelegatedBy(nodes, permissions),
    matterScopedPermissions,
  };
}

/** Build the registry from an array of ActionAttachment instances */
export function buildRegistry<const Rs extends readonly ActionAttachment[]>(
  ...nodes: Rs
): ProcessedRegistryOutput<Rs> {
  return processRegistry(nodes) as ProcessedRegistryOutput<Rs>;
}
